import { ConceptState } from './model';

/**
 * A specific property on a atom that can me styled. Generally maps to css
 * properties such as color, background, border-color etc.
 */
export type Shade = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type Chromatic = 1 | 2 | 3 | 4 | 5;
export type PaletteName = 'neutral' | `chromatic${Chromatic}`;
export type ShadeKey =
  | `neutral-shade-${0 | Shade}`
  | `chromatic${Chromatic}-shade-${Shade}`;

export const OPACITY_MODIFIERS = [5, 10, 15, 20, 30, 40, 50, 60] as const;
type OpacityModifier = (typeof OPACITY_MODIFIERS)[number];

export const NEUTRAL_DARKEST = 'neutral-shade-7';

export type ColorVariable =
  | 'none'
  | ShadeKey
  | 'neutral-white'
  | 'transparent'
  | `neutral-white-${OpacityModifier}`
  | `neutral-shade-7-${OpacityModifier}`;

export type SchemeProperty =
  | 'background'
  | 'foreground'
  | 'text'
  | 'accent'
  | 'border';
export type SchemeVariable = `color-scheme-1-${SchemeProperty}`;

export type ConceptVariable =
  | 'button-text'
  | 'light-button-text'
  | 'light-button-background'
  | 'alternate-button-text'
  | 'alternate-button-background';

// @Deprecated, put these somewhere else
export interface ConceptStyleGuide {
  imageOverrides: ImageOverrides;
}

export type ShadeDescriptor = (
  | { readonly palette: 'neutral'; readonly chromatic?: undefined }
  | { readonly palette: 'chromatic'; readonly chromatic: number }
) & { readonly shade: number };

const ShadeRegEx = /(?<palette>[A-z]+)(?<chromatic>\d)?-shade-(?<shade>\d)/;
export function parseShadeKey(shadeKey: string): ShadeDescriptor | undefined {
  const match = ShadeRegEx.exec(shadeKey);
  if (match?.groups == null) {
    return undefined;
  }

  const { palette, chromatic: chromaticStr, shade: shadeStr } = match.groups;
  const shade = parseInt(shadeStr, 10);
  if (palette === 'neutral') {
    return {
      palette,
      shade,
    };
  } else if (palette === 'chromatic' && chromaticStr != null) {
    const chromatic = parseInt(chromaticStr, 10);
    return {
      palette,
      chromatic,
      shade,
    };
  }

  return undefined;
}

export type AlternateTarget =
  | 'primary-button'
  | 'tagline'
  | 'form-input'
  | 'logo';

export type CompiledScheme = ShadeDescriptor & {
  readonly background: ColorVariable;
  readonly foreground: ColorVariable;
  readonly text: ColorVariable;
  readonly accent: ColorVariable;
  readonly border: ColorVariable;
  readonly alternate: ReadonlySet<AlternateTarget>;
  /** @deprecated prefer using an `alternate` target. */
  readonly isDark: boolean;
};
export type CompiledChromaticColor = Readonly<{
  palette: 'chromatic';
  name: string;
  hex: string;
  shades: ChromaticShades;
  chromatic: number;
  locked: boolean;
}>;
export type CompiledNeutralColor = Readonly<{
  palette: 'neutral';
  hex: string;
  shades: NeutralShades;
  locked: boolean;
  chromatic?: never;
}>;

/**
 * Neutral palettes have 8 shades
 */
export type NeutralShades = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

/**
 * Chromatic palettes have 7 shades
 */
export type ChromaticShades = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];
export type ImageOverride = {
  src: string;
  srcset: string;
  blurredSrc?: string;
};
export type ImageOverrides = { [key: string]: ImageOverride };
export interface CompiledConcept {
  state: ConceptState;
  chromatics: ReadonlyArray<CompiledChromaticColor>;
  neutral: CompiledNeutralColor;
  schemes: ReadonlyArray<CompiledScheme>;
  colorVariables: ReadonlyMap<ColorVariable, string>;
}
